import { Badge, Button, Collapsible, Container, Typography } from '@timberhubcom/forest'
import { useEffect, useState } from 'react'

import { ZendeskTicket } from '@/models/zendeskTicket.model.ts'
import { useTruckReducer } from '@/providers'
import { getZendeskTicketsForTruck } from '@/services/zendeskTickets/zendeskTickets.service.ts'

/**
 * Compact card listing Zendesk support tickets that reference this truck
 * (matched on Order Truck ID or Purchase Truck ID custom fields).
 *
 * Source: timberhub-ops zendesk_tickets table, populated by the
 * nous-timberhub `zendesk-ticket-sync` schedule every 5 minutes.
 */
export const ZendeskTicketsComponent = () => {
  const truck = useTruckReducer()
  const [tickets, setTickets] = useState<ZendeskTicket[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showClosed, setShowClosed] = useState(false)

  useEffect(() => {
    let cancelled = false
    setTickets(null)
    setError(null)
    getZendeskTicketsForTruck(truck.id)
      .then(res => {
        if (!cancelled) {
          setTickets(res.data.tickets)
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err?.message || 'Failed to load tickets')
        }
      })
    return () => {
      cancelled = true
    }
  }, [truck.id])

  const visible = (tickets || []).filter(t =>
    showClosed ? true : !['closed', 'solved'].includes((t.status || '').toLowerCase())
  )
  const openCount = (tickets || []).filter(t => !['closed', 'solved'].includes((t.status || '').toLowerCase())).length

  const statusIntent = (s: string): 'error' | 'warning' | 'success' | 'grey' => {
    switch ((s || '').toLowerCase()) {
      case 'new':
      case 'open':
        return 'error'
      case 'pending':
        return 'warning'
      case 'solved':
      case 'closed':
        return 'success'
      default:
        return 'grey'
    }
  }

  return (
    <Collapsible
      isDefaultOpened={openCount > 0}
      title={`Zendesk tickets${tickets ? ` (${openCount})` : ''}`}
      addonButtonProps={{
        children: showClosed ? 'Hide closed' : 'Show closed',
        variant: 'primary',
        color: 'grey',
        onClick: () => setShowClosed(prev => !prev),
      }}>
      <Container gap={2}>
        {error && (
          <Typography variant={'body_s'} className={'text-red-600'}>
            {error}
          </Typography>
        )}

        {!error && tickets === null && (
          <Typography variant={'body_s'} className={'text-grey-overlay-500'}>
            Loading…
          </Typography>
        )}

        {!error && tickets !== null && visible.length === 0 && (
          <Typography variant={'body_s'} className={'py-4 text-center text-grey-overlay-500'}>
            {openCount === 0 && tickets.length === 0
              ? 'No tickets reference this truck.'
              : 'No open tickets. Toggle "Show closed" to see history.'}
          </Typography>
        )}

        {!error &&
          visible.map(t => (
            <Container
              key={t.zendesk_id}
              direction={'horizontal'}
              gap={3}
              className={'flex flex-row items-center justify-between border-b border-grey-overlay-100 pb-2'}>
              <Container className={'min-w-0 flex-1'} gap={1}>
                <Container direction={'horizontal'} gap={2} className={'flex flex-row items-center'}>
                  <Typography variant={'headline_ss_xs'} className={'truncate'}>
                    {t.subject || '(no subject)'}
                  </Typography>
                  <Badge intent={statusIntent(t.status)} className={'capitalize'}>
                    {t.status}
                  </Badge>
                </Container>
                <Container
                  direction={'horizontal'}
                  gap={2}
                  className={'flex flex-row items-center text-xs text-grey-overlay-500'}>
                  {t.priority && <span className={'capitalize'}>{t.priority}</span>}
                  {t.order_milestone && <span>· {t.order_milestone}</span>}
                  {t.is_order_truck_match && (
                    <Badge intent={'info'} className={'text-[10px] uppercase'}>
                      Order
                    </Badge>
                  )}
                  {t.is_purchase_truck_match && (
                    <Badge intent={'primary'} className={'text-[10px] uppercase'}>
                      Purchase
                    </Badge>
                  )}
                </Container>
              </Container>
              {t.url && (
                <a href={t.url} target={'_blank'} rel={'noopener noreferrer'} className={'shrink-0'}>
                  <Button color={'grey'} variant={'icon'} size={'xs'} icon={'ArrowSquareOut'} />
                </a>
              )}
            </Container>
          ))}
      </Container>
    </Collapsible>
  )
}
